import { useUserContext } from '@context/UserContext';
import { formatCNPJ, formateCpf, formatPhone } from '@hooks/masks';
import {
  createZendeskUser,
  getCurrentTicketNumber,
  getCustomObjects,
  popupError,
  zafClient,
} from '@services/Zendesk';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CustomObjects } from 'types/CustomObjects';

export function useReviewUserAccount() {
  const { selectedUser, selectedRole, urlCustom, setUrlCustom, setNewUserFormData } =
    useUserContext();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const documentLabel = selectedUser?.user_fields?.cpf_3
    ? 'CPF'
    : selectedUser?.user_fields?.cnpj_regex
      ? 'CNPJ'
      : 'Documento';
  const documentValue = selectedUser?.user_fields?.cpf_3
    ? formateCpf(selectedUser?.user_fields?.cpf_3)
    : selectedUser?.user_fields?.cnpj_regex
      ? formatCNPJ(selectedUser?.user_fields?.cnpj_regex || '')
      : '—';
  const phoneValue = formatPhone(selectedUser?.phone || '') || '—';

  const info = [
    { label: `${selectedUser?.user_fields?.cnpj_regex ? 'Razão social' : 'Nome'}`, value: selectedUser?.name || selectedUser?.user_fields._pessoa_juridica__razao_social },
    { label: documentLabel, value: documentValue },
    { label: 'Email', value: selectedUser?.email },
    { label: 'Telefone', value: phoneValue },
    { label: 'Tipo de conta', value: selectedRole },
    { label: 'Endereço', value: selectedUser?.user_fields.endereco_2 },
  ];

  const createUser = async () => {
    if (!selectedUser || !selectedRole) return;

    try {
      if (selectedUser.id) {
        return selectedUser;
      }

      const updatedUserFields = {
        ...selectedUser.user_fields,
        perfil_da_conta: selectedRole,
        nao_possui_e_mail: !selectedUser.email,
      };

      if (!selectedUser.email) {
        selectedUser.user_fields.nao_possui_e_mail = true;
      }

      const userData: any = {
        name: selectedUser.name || selectedUser.user_fields._pessoa_juridica__razao_social,
        email: selectedUser.email,
        phone: selectedUser.phone,
        external_id: `${selectedUser.user_fields.cpf_3?.replace(/\D/g, '') || selectedUser.user_fields.cnpj_regex?.replace(/\D/g, '')}`,
        user_fields: updatedUserFields,
      };

      if (selectedUser.user_fields?.cpf_3) {
        userData.user_fields.cpf_3 = String(selectedUser.user_fields.cpf_3).replace(/\D/g, '');
      } else if (selectedUser.user_fields?.cnpj_regex) {
        userData.user_fields.cnpj_regex = String(selectedUser.user_fields.cnpj_regex).replace(/\D/g, '');
      }

      const createdUser = await createZendeskUser(zafClient, userData);
      return createdUser;
    } catch (error: any) {
      console.error('Error creating user:', error);
      if (error.status === 403) {
        popupError("Você não tem permissão para criar usuários. Por favor, contate o administrador.", true);
        throw error;
      }
      popupError(error.responseJSON?.details?.cpf_3?.[0]?.description, true);
      throw error;
    }
  };

  const handleSave = async (shouldLink = true) => {
    if (!selectedUser || !selectedRole) return;

    try {
      setLoading(true);
      const userToSave = selectedUser?.id ? selectedUser : await createUser();

      if (!selectedUser.id && !shouldLink) {
        navigate('/user-success-account');
        setNewUserFormData({ name: '', cpf_3: '', phone: '', email: '', cnpj_regex: '', tipoConta: '' });
        return;
      }

      const ticket = await getCurrentTicketNumber(zafClient);
      const customObjects = await getCustomObjects(zafClient, ticket as number);

      let customObject = customObjects.find(
        (obj: { external_id: string }) => obj.external_id === `ticket-${ticket}`
      );

      if (customObject) {
        const currentFields = customObject.custom_object_fields || {};

        const updatedFields = {
          ...currentFields,
          [selectedRole]: userToSave?.id?.toString(),
        };

        await zafClient.request({
          url: customObject.url,
          type: 'PUT',
          data: {
            custom_object_record: {
              custom_object_fields: updatedFields,
            },
          },
        });
      } else {
        const response = await zafClient.request({
          url: `/api/v2/custom_objects/contas_do_ticket/records/${customObjects[0].id}`,
          type: 'PUT',
          data: {
            custom_object_record: {
              custom_object_id: customObjects[0].id,
              custom_object_fields: {
                [selectedRole]: userToSave?.id?.toString(),
              },
            },
          },
        });

        const custom = response as CustomObjects;
        customObject = custom.custom_object_record;
      }

      if (customObject.url && !urlCustom.includes(customObject.url)) {
        setUrlCustom([...urlCustom, customObject.url]);
      }

      if (selectedRole === 'cliente') {
        await zafClient.request({
          url: `/api/v2/tickets/${ticket}.json`,
          type: 'PUT',
          data: {
            ticket: {
              requester_id: userToSave?.id,
            }
          }
        });
      }

      navigate('/user-success-account');
      setNewUserFormData({ name: '', cpf_3: '', phone: '', email: '', cnpj_regex: '', tipoConta: '' });
    } catch (error) {
      {
        console.error('Error saving user account:', error);
        const errorResponse = error as any;
        const description =
          errorResponse.responseJSON?.details?.["custom_field_values.base"]?.[0]?.description;
        popupError(description, true);
        const ticketError = errorResponse.responseJSON?.details?.base?.[0]?.description;
        const emailError = errorResponse.responseJSON?.details?.email?.[0]?.description;
        popupError(`Erro ao editar o solicitante: ${emailError}`, true);
        popupError(`Erro ao editar o solicitante: ${ticketError}`, true);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    selectedUser,
    info,
    navigate,
    handleSave,
    selectedRole,
    loading,
  };
}
